<script setup>
/* eslint-disable vue/no-bare-strings-in-template */
/* eslint-disable @intlify/vue-i18n/no-raw-text */
/* eslint-disable no-alert */
/* eslint-disable no-use-before-define */
import { computed, onMounted, reactive, ref } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import SalesReportAPI from 'dashboard/api/salesReport';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';
import Button from 'dashboard/components-next/button/Button.vue';
import CommercialRecordEditModal from 'dashboard/components-next/ConversationWorkflow/CommercialRecordEditModal.vue';

const store = useStore();
const currentUser = useMapGetter('getCurrentUser');
const reopeningConversationId = ref(null);
const isAdministrator = computed(
  () => currentUser.value?.role === 'administrator'
);

const isLoading = ref(false);
const isDownloadingPdf = ref(false);
const isEmailingReport = ref(false);
const payingConversationId = ref(null);
const invoicingConversationId = ref(null);
const editModalRef = ref(null);
const expandedMonths = reactive({});
const monthDetails = reactive({});
const loadingMonths = reactive({});
const providerCatalogs = ref([]);
const uploadingCatalogProviderId = ref(null);
const downloadingCatalogProviderId = ref(null);
const deletingCatalogProviderId = ref(null);
const catalogsExpanded = ref(false);
const routingExpanded = ref(false);
const routingProviders = ref([]);
const providerRoutes = ref([]);
const routeDrafts = reactive({});
const savingRoutingCity = ref(null);
const isReassigningProvider = ref(false);
const reassignmentForm = reactive({
  conversation_id: '',
  provider_id: '',
  resend_catalog: false,
});

const dayOptions = [
  { value: 1, label: 'L' },
  { value: 2, label: 'M' },
  { value: 3, label: 'X' },
  { value: 4, label: 'J' },
  { value: 5, label: 'V' },
  { value: 6, label: 'S' },
  { value: 0, label: 'D' },
];

const report = reactive({
  summary: {
    total_closed: 0,
    sales_count: 0,
    no_sales_count: 0,
    total_sales_amount: 0,
    total_commission: 0,
    pending_commission: 0,
    invoiced_commission: 0,
    paid_commission: 0,
    conversion_rate: 0,
  },
  no_sale_reasons: {},
  providers: [],
  monthly_history: [],
  records: [],
  pagination: {
    page: 1,
    per_page: 25,
    total: 0,
    total_pages: 0,
  },
});

const filters = reactive({
  from: '',
  to: '',
  result: '',
  provider_id: '',
  commission_status: '',
  page: 1,
  per_page: 25,
});

const tableHeaders = [
  'Conversación',
  'Proveedor',
  'Resultado',
  'Producto / Motivo',
  'Importe',
  'Comisión',
  'Estado',
  'Fecha de cierre',
  'Acción',
];

const noSaleReasonLabels = {
  no_response: 'El cliente dejó de contestar',
  too_expensive: 'Le pareció caro',
  information_only: 'Solo quería información',
  unavailable: 'No había disponibilidad',
  outside_service_area: 'Fuera de zona de servicio',
  bought_elsewhere: 'Compró en otro sitio',
  unavailable_date: 'Fecha u horario no disponible',
  other: 'Otro',
};

const summaryCards = computed(() => [
  {
    label: 'Ventas',
    value: formatMoney(report.summary.total_sales_amount),
  },
  {
    label: 'Comisión pendiente',
    value: formatMoney(report.summary.pending_commission),
  },
  {
    label: 'Comisión facturada',
    value: formatMoney(report.summary.invoiced_commission),
  },
  {
    label: 'Comisión pagada',
    value: formatMoney(report.summary.paid_commission),
  },
  {
    label: 'Conversión',
    value: `${report.summary.conversion_rate || 0} %`,
  },
  {
    label: 'Ventas cerradas',
    value: report.summary.sales_count || 0,
  },
  {
    label: 'No ventas',
    value: report.summary.no_sales_count || 0,
  },
]);

const pendingVisibleIds = computed(() =>
  report.records
    .filter(
      record =>
        record.result === 'sale' && record.commission_status === 'pending'
    )
    .map(record => record.id)
);

const hasPreviousPage = computed(() => report.pagination.page > 1);
const hasNextPage = computed(
  () => report.pagination.page < report.pagination.total_pages
);

const formatMoney = value =>
  new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
  }).format(Number(value || 0));

const formatDate = value => {
  if (!value) return '—';

  return new Intl.DateTimeFormat('es-MX', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
};

const resultLabel = result => {
  if (result === 'sale') return 'Venta';
  if (result === 'no_sale') return 'No venta';
  return '—';
};

const commissionStatusLabel = status => {
  if (status === 'pending') return 'Pendiente';
  if (status === 'invoiced') return 'Facturada';
  if (status === 'paid') return 'Pagada';
  return '—';
};

const loadReport = async () => {
  isLoading.value = true;

  try {
    const { data } = await SalesReportAPI.getReport(filters);
    const payload = data.payload || {};

    report.summary = payload.summary || report.summary;
    report.no_sale_reasons = payload.no_sale_reasons || {};
    report.providers = payload.providers || [];
    report.monthly_history = payload.monthly_history || [];
    report.records = payload.records || [];
    report.pagination = payload.pagination || report.pagination;
  } catch (error) {
    useAlert(
      error.response?.data?.error || 'No se pudo cargar el informe de ventas.'
    );
  } finally {
    isLoading.value = false;
  }
};

const applyFilters = () => {
  filters.page = 1;
  loadReport();
};

const clearFilters = () => {
  filters.from = '';
  filters.to = '';
  filters.result = '';
  filters.provider_id = '';
  filters.commission_status = '';
  filters.page = 1;
  loadReport();
};

const previousPage = () => {
  if (!hasPreviousPage.value) return;
  filters.page -= 1;
  loadReport();
};

const nextPage = () => {
  if (!hasNextPage.value) return;
  filters.page += 1;
  loadReport();
};

const markPaid = async record => {
  if (
    !window.confirm(
      `¿Marcar como pagada la comisión de ${formatMoney(
        record.commission_amount
      )}?`
    )
  ) {
    return;
  }

  payingConversationId.value = record.id;

  try {
    await SalesReportAPI.markPaid(record.id);
    useAlert('La comisión se ha marcado como pagada.');
    await loadReport();
    await refreshExpandedMonths();
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        'No se pudo marcar la comisión como pagada.'
    );
  } finally {
    payingConversationId.value = null;
  }
};

const formatMonth = value => {
  if (!value) return '';

  const [year, month] = value.split('-');
  const date = new Date(Number(year), Number(month) - 1, 1);

  return new Intl.DateTimeFormat('es-MX', {
    month: 'long',
    year: 'numeric',
  }).format(date);
};

const loadMonth = async month => {
  loadingMonths[month] = true;

  try {
    const { data } = await SalesReportAPI.getReport({
      month,
      provider_id: filters.provider_id,
      page: 1,
      per_page: 100,
    });
    monthDetails[month] = data.payload?.records || [];
  } catch (error) {
    useAlert(
      error.response?.data?.error || 'No se pudo cargar el detalle del mes.'
    );
  } finally {
    loadingMonths[month] = false;
  }
};

const toggleMonth = async month => {
  expandedMonths[month] = !expandedMonths[month];

  if (expandedMonths[month]) {
    await loadMonth(month);
  }
};

const refreshExpandedMonths = async () => {
  const months = Object.keys(expandedMonths).filter(
    month => expandedMonths[month]
  );
  await Promise.all(months.map(loadMonth));
};

const markInvoiced = async conversationIds => {
  if (!conversationIds.length) return;

  if (
    !window.confirm(
      `¿Marcar ${conversationIds.length} venta(s) como facturadas?`
    )
  ) {
    return;
  }

  invoicingConversationId.value =
    conversationIds.length === 1 ? conversationIds[0] : 'bulk';

  try {
    await SalesReportAPI.markInvoiced(conversationIds);
    useAlert('Las comisiones se han marcado como facturadas.');
    await loadReport();
    await refreshExpandedMonths();
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        'No se pudieron marcar las comisiones como facturadas.'
    );
  } finally {
    invoicingConversationId.value = null;
  }
};

const openEditModal = record => {
  editModalRef.value?.open(record);
};

const updateCommercialRecord = async ({ conversationId, record }) => {
  try {
    await SalesReportAPI.updateCommercialRecord(conversationId, record);
    useAlert('El registro comercial se ha actualizado.');
    await loadReport();
    await refreshExpandedMonths();
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        'No se pudo actualizar el registro comercial.'
    );
  }
};

const reopenConversation = async record => {
  if (
    !window.confirm(
      `¿Reabrir la conversación #${record.id}? Volverá a aparecer en la bandeja de entrada.`
    )
  ) {
    return;
  }

  reopeningConversationId.value = record.id;

  try {
    await store.dispatch('toggleStatus', {
      conversationId: record.id,
      status: 'open',
    });
    useAlert(`La conversación #${record.id} se ha reabierto.`);
  } catch (error) {
    useAlert(
      error.response?.data?.error || 'No se pudo reabrir la conversación.'
    );
  } finally {
    reopeningConversationId.value = null;
  }
};

const deleteCommercialRecord = async record => {
  if (
    !window.confirm(
      `¿Eliminar el registro comercial de la conversación #${record.id}? La conversación y sus mensajes se conservarán.`
    )
  ) {
    return;
  }

  try {
    await SalesReportAPI.deleteCommercialRecord(record.id);
    useAlert('El registro se ha eliminado del informe comercial.');
    await loadReport();
    await refreshExpandedMonths();
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        'No se pudo eliminar el registro comercial.'
    );
  }
};

const formatFileSize = value => {
  const bytes = Number(value || 0);
  if (!bytes) return '—';

  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
};

const loadProviderCatalogs = async () => {
  try {
    const { data } = await SalesReportAPI.getProviderCatalogs();
    providerCatalogs.value = data.payload || [];
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        'No se pudieron cargar los catálogos de proveedores.'
    );
  }
};

const uploadProviderCatalog = async (event, provider) => {
  const [file] = event.target.files || [];
  event.target.value = '';
  if (!file) return;

  if (file.type !== 'application/pdf') {
    useAlert('Selecciona un archivo PDF.');
    return;
  }

  if (
    provider.attached &&
    !window.confirm(
      `¿Sustituir el catálogo actual de ${provider.provider_name}?`
    )
  ) {
    return;
  }

  uploadingCatalogProviderId.value = provider.provider_id;

  try {
    await SalesReportAPI.uploadProviderCatalog(provider.provider_id, file);
    useAlert('El catálogo se ha guardado correctamente.');
    await loadProviderCatalogs();
  } catch (error) {
    useAlert(error.response?.data?.error || 'No se pudo guardar el catálogo.');
  } finally {
    uploadingCatalogProviderId.value = null;
  }
};

const downloadProviderCatalog = async provider => {
  downloadingCatalogProviderId.value = provider.provider_id;

  try {
    const { data } = await SalesReportAPI.downloadProviderCatalog(
      provider.provider_id
    );
    const url = window.URL.createObjectURL(data);
    const link = document.createElement('a');
    link.href = url;
    link.download = provider.filename || 'catalogo.pdf';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    useAlert('No se pudo descargar el catálogo.');
  } finally {
    downloadingCatalogProviderId.value = null;
  }
};

const deleteProviderCatalog = async provider => {
  if (!window.confirm(`¿Eliminar el catálogo de ${provider.provider_name}?`)) {
    return;
  }

  deletingCatalogProviderId.value = provider.provider_id;

  try {
    await SalesReportAPI.deleteProviderCatalog(provider.provider_id);
    useAlert('El catálogo se ha eliminado.');
    await loadProviderCatalogs();
  } catch (error) {
    useAlert(error.response?.data?.error || 'No se pudo eliminar el catálogo.');
  } finally {
    deletingCatalogProviderId.value = null;
  }
};

const loadProviderRouting = async () => {
  try {
    const { data } = await SalesReportAPI.getProviderRouting();
    const payload = data.payload || {};

    routingProviders.value = payload.providers || [];
    providerRoutes.value = payload.routes || [];

    Object.keys(routeDrafts).forEach(key => {
      delete routeDrafts[key];
    });

    providerRoutes.value.forEach(route => {
      routeDrafts[route.city] = JSON.parse(JSON.stringify(route));
    });
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        'No se pudo cargar la asignación de proveedores.'
    );
  }
};

const confirmedRoutingProviders = computed(() =>
  routingProviders.value.filter(provider => provider.confirmed)
);

const addSchedule = city => {
  routeDrafts[city].schedules.push({
    provider_id: '',
    days: [1, 2, 3, 4, 5],
    start_time: '09:00',
    end_time: '19:00',
  });
};

const removeSchedule = (city, index) => {
  routeDrafts[city].schedules.splice(index, 1);
};

const toggleScheduleDay = (schedule, day) => {
  const index = schedule.days.indexOf(day);

  if (index === -1) {
    schedule.days.push(day);
    schedule.days.sort((a, b) => a - b);
  } else {
    schedule.days.splice(index, 1);
  }
};

const saveProviderRoute = async city => {
  const draft = routeDrafts[city];
  if (!draft) return;

  savingRoutingCity.value = city;

  try {
    const { data } = await SalesReportAPI.updateProviderRouting({
      city,
      inbox_id: draft.inbox_id,
      mode: draft.mode,
      timezone: draft.timezone,
      manual_provider_id: draft.manual_provider_id,
      fallback_provider_id: draft.fallback_provider_id,
      schedules: draft.schedules,
    });

    const savedRoute = data.payload;
    const routeIndex = providerRoutes.value.findIndex(
      route => route.city === city
    );

    if (routeIndex !== -1) {
      providerRoutes.value[routeIndex] = savedRoute;
    }

    routeDrafts[city] = JSON.parse(JSON.stringify(savedRoute));
    useAlert(`La asignación de ${city} se ha actualizado.`);
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        'No se pudo actualizar la asignación del proveedor.'
    );
  } finally {
    savingRoutingCity.value = null;
  }
};

const reassignProvider = async () => {
  if (!reassignmentForm.conversation_id || !reassignmentForm.provider_id) {
    useAlert('Indica la conversación y el nuevo proveedor.');
    return;
  }

  const provider = routingProviders.value.find(
    item => item.id === reassignmentForm.provider_id
  );

  if (
    !window.confirm(
      `¿Reasignar la conversación #${reassignmentForm.conversation_id} a ${provider?.name || 'este proveedor'}?`
    )
  ) {
    return;
  }

  isReassigningProvider.value = true;

  try {
    const { data } = await SalesReportAPI.reassignProvider({
      conversation_id: reassignmentForm.conversation_id,
      provider_id: reassignmentForm.provider_id,
      resend_catalog: reassignmentForm.resend_catalog,
    });

    const payload = data.payload || {};
    useAlert(
      `La conversación #${payload.conversation_id} se ha asignado a ${payload.provider_name}.`
    );

    reassignmentForm.conversation_id = '';
    reassignmentForm.provider_id = '';
    reassignmentForm.resend_catalog = false;

    await loadReport();
    await refreshExpandedMonths();
  } catch (error) {
    useAlert(
      error.response?.data?.error || 'No se pudo reasignar la conversación.'
    );
  } finally {
    isReassigningProvider.value = false;
  }
};

const billingReportFilters = () => ({
  from: filters.from,
  to: filters.to,
  provider_id: filters.provider_id,
  commission_status: filters.commission_status,
});

const downloadBillingPdf = async () => {
  if (!filters.provider_id) {
    useAlert('Selecciona un proveedor para descargar su informe.');
    return;
  }

  isDownloadingPdf.value = true;

  try {
    const response = await SalesReportAPI.downloadPdf(billingReportFilters());
    const disposition = response.headers['content-disposition'] || '';
    const filenameMatch = disposition.match(/filename="?([^";]+)"?/);
    const filename =
      filenameMatch?.[1] || `informe-facturacion-${filters.provider_id}.pdf`;

    const url = window.URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    useAlert('El informe PDF se ha descargado.');
  } catch (error) {
    useAlert('No se pudo descargar el informe PDF.');
  } finally {
    isDownloadingPdf.value = false;
  }
};

const emailBillingReport = async () => {
  if (!filters.provider_id) {
    useAlert('Selecciona un proveedor para enviar su informe.');
    return;
  }

  if (
    !window.confirm(
      '¿Enviar por correo al proveedor el informe correspondiente a los filtros actuales?'
    )
  ) {
    return;
  }

  isEmailingReport.value = true;

  try {
    const { data } = await SalesReportAPI.emailReport(billingReportFilters());
    useAlert(
      data.message ||
        'El informe se ha preparado y enviado al correo del proveedor.'
    );
  } catch (error) {
    useAlert(
      error.response?.data?.error || 'No se pudo enviar el informe por correo.'
    );
  } finally {
    isEmailingReport.value = false;
  }
};

onMounted(() => {
  loadReport();
  loadProviderCatalogs();
  loadProviderRouting();
});
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    loading-message="Cargando ventas y comisiones..."
    :no-records-found="false"
  >
    <template #header>
      <BaseSettingsHeader
        title="Ventas y comisiones"
        description="Control de cierres, ventas y liquidaciones de proveedores."
        feature-name="sales-report"
      />
    </template>

    <template #body>
      <div class="flex flex-col gap-6 mt-4">
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          <div
            v-for="card in summaryCards"
            :key="card.label"
            class="p-4 border rounded-xl border-n-weak bg-n-solid-2"
          >
            <div class="text-sm text-n-slate-11">{{ card.label }}</div>
            <div class="mt-1 text-xl font-semibold text-n-slate-12">
              {{ card.value }}
            </div>
          </div>
        </div>

        <div class="p-4 border rounded-xl border-n-weak">
          <div class="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
            <label class="flex flex-col gap-1 text-sm text-n-slate-11">
              Desde
              <input
                v-model="filters.from"
                type="date"
                class="h-10 px-3 border rounded-lg border-n-weak bg-n-alpha-2 text-n-slate-12"
              />
            </label>

            <label class="flex flex-col gap-1 text-sm text-n-slate-11">
              Hasta
              <input
                v-model="filters.to"
                type="date"
                class="h-10 px-3 border rounded-lg border-n-weak bg-n-alpha-2 text-n-slate-12"
              />
            </label>

            <label class="flex flex-col gap-1 text-sm text-n-slate-11">
              Resultado
              <select
                v-model="filters.result"
                class="h-10 px-3 border rounded-lg border-n-weak bg-n-alpha-2 text-n-slate-12"
              >
                <option value="">Todos</option>
                <option value="sale">Venta</option>
                <option value="no_sale">No venta</option>
              </select>
            </label>

            <label class="flex flex-col gap-1 text-sm text-n-slate-11">
              Proveedor
              <select
                v-model="filters.provider_id"
                class="h-10 px-3 border rounded-lg border-n-weak bg-n-alpha-2 text-n-slate-12"
              >
                <option value="">Todos</option>
                <option
                  v-for="provider in report.providers"
                  :key="provider.id"
                  :value="provider.id"
                >
                  {{ provider.name }}
                </option>
              </select>
            </label>

            <label class="flex flex-col gap-1 text-sm text-n-slate-11">
              Comisión
              <select
                v-model="filters.commission_status"
                class="h-10 px-3 border rounded-lg border-n-weak bg-n-alpha-2 text-n-slate-12"
              >
                <option value="">Todas</option>
                <option value="pending">Pendiente</option>
                <option value="invoiced">Facturada</option>
                <option value="paid">Pagada</option>
              </select>
            </label>
          </div>

          <div class="flex flex-wrap gap-2 mt-4">
            <Button label="Aplicar filtros" size="sm" @click="applyFilters" />
            <Button
              v-if="filters.provider_id"
              label="Descargar PDF"
              size="sm"
              color="slate"
              :is-loading="isDownloadingPdf"
              @click="downloadBillingPdf"
            />
            <Button
              v-if="filters.provider_id"
              label="Enviar informe por correo"
              size="sm"
              color="slate"
              :is-loading="isEmailingReport"
              @click="emailBillingReport"
            />
            <Button
              v-if="pendingVisibleIds.length"
              :label="`Facturar pendientes visibles (${pendingVisibleIds.length})`"
              size="sm"
              color="slate"
              :is-loading="invoicingConversationId === 'bulk'"
              @click="markInvoiced(pendingVisibleIds)"
            />
            <Button
              label="Limpiar"
              size="sm"
              variant="ghost"
              color="slate"
              @click="clearFilters"
            />
          </div>
        </div>

        <div class="overflow-x-auto border rounded-xl border-n-weak">
          <BaseTable
            :headers="tableHeaders"
            :items="report.records"
            :loading="isLoading"
            no-data-message="No hay cierres que coincidan con los filtros."
          >
            <template #row="{ items }">
              <BaseTableRow
                v-for="record in items"
                :key="record.id"
                :item="record"
              >
                <template #default>
                  <BaseTableCell>
                    <div class="font-medium text-n-slate-12">
                      #{{ record.id }}
                    </div>
                    <div class="text-xs text-n-slate-10">
                      {{ record.contact_name || 'Sin nombre' }}
                    </div>
                  </BaseTableCell>

                  <BaseTableCell>
                    {{ record.provider_name || '—' }}
                  </BaseTableCell>

                  <BaseTableCell>
                    <span
                      class="inline-flex px-2 py-1 text-xs rounded-full"
                      :class="
                        record.result === 'sale'
                          ? 'bg-n-teal-3 text-n-teal-11'
                          : 'bg-n-amber-3 text-n-amber-11'
                      "
                    >
                      {{ resultLabel(record.result) }}
                    </span>
                  </BaseTableCell>

                  <BaseTableCell>
                    {{
                      record.result === 'sale'
                        ? record.product_name
                        : noSaleReasonLabels[record.no_sale_reason] || '—'
                    }}
                  </BaseTableCell>

                  <BaseTableCell>
                    {{
                      record.result === 'sale'
                        ? formatMoney(record.product_amount)
                        : '—'
                    }}
                  </BaseTableCell>

                  <BaseTableCell>
                    {{
                      record.result === 'sale'
                        ? formatMoney(record.commission_amount)
                        : '—'
                    }}
                  </BaseTableCell>

                  <BaseTableCell>
                    {{ commissionStatusLabel(record.commission_status) }}
                  </BaseTableCell>

                  <BaseTableCell>
                    {{ formatDate(record.closed_at) }}
                  </BaseTableCell>

                  <BaseTableCell>
                    <div class="flex flex-wrap gap-1">
                      <Button
                        v-if="
                          record.result === 'sale' &&
                          record.commission_status === 'pending'
                        "
                        label="Facturar"
                        size="sm"
                        color="slate"
                        :is-loading="invoicingConversationId === record.id"
                        @click="markInvoiced([record.id])"
                      />
                      <Button
                        v-if="
                          record.result === 'sale' &&
                          record.commission_status === 'invoiced'
                        "
                        label="Marcar pagada"
                        size="sm"
                        color="slate"
                        :is-loading="payingConversationId === record.id"
                        @click="markPaid(record)"
                      />
                      <Button
                        v-if="isAdministrator"
                        label="Reabrir conversación"
                        size="sm"
                        variant="ghost"
                        color="slate"
                        :is-loading="reopeningConversationId === record.id"
                        @click="reopenConversation(record)"
                      />
                      <Button
                        label="Editar"
                        size="sm"
                        variant="ghost"
                        color="slate"
                        @click="openEditModal(record)"
                      />
                      <Button
                        label="Eliminar"
                        size="sm"
                        variant="ghost"
                        color="slate"
                        @click="deleteCommercialRecord(record)"
                      />
                    </div>
                  </BaseTableCell>
                </template>
              </BaseTableRow>
            </template>
          </BaseTable>
        </div>

        <div class="flex items-center justify-between">
          <div class="text-sm text-n-slate-11">
            {{ report.pagination.total }} cierres · Página
            {{ report.pagination.page }} de
            {{ report.pagination.total_pages || 1 }}
          </div>

          <div class="flex gap-2">
            <Button
              label="Anterior"
              size="sm"
              variant="ghost"
              color="slate"
              :disabled="!hasPreviousPage"
              @click="previousPage"
            />
            <Button
              label="Siguiente"
              size="sm"
              variant="ghost"
              color="slate"
              :disabled="!hasNextPage"
              @click="nextPage"
            />
          </div>
        </div>

        <div
          v-if="Object.keys(report.no_sale_reasons).length"
          class="p-4 border rounded-xl border-n-weak"
        >
          <h3 class="mb-3 text-base font-semibold text-n-slate-12">
            Motivos de no venta
          </h3>
          <div class="flex flex-wrap gap-2">
            <span
              v-for="(count, reason) in report.no_sale_reasons"
              :key="reason"
              class="px-3 py-2 text-sm rounded-lg bg-n-alpha-2 text-n-slate-11"
            >
              {{ noSaleReasonLabels[reason] || reason }}: {{ count }}
            </span>
          </div>
        </div>

        <section class="flex flex-col gap-3">
          <div class="flex items-center justify-between">
            <div>
              <h3 class="text-lg font-semibold text-n-slate-12">
                Histórico comercial por meses
              </h3>
              <p class="text-sm text-n-slate-11">
                Ventas y no ventas agrupadas por fecha de cierre.
              </p>
            </div>
          </div>

          <div
            v-if="!report.monthly_history.length"
            class="p-6 text-center border rounded-xl border-n-weak text-n-slate-11"
          >
            Todavía no hay meses con registros comerciales.
          </div>

          <div
            v-for="month in report.monthly_history"
            :key="month.month"
            class="overflow-hidden border rounded-xl border-n-weak"
          >
            <button
              type="button"
              class="flex flex-wrap items-center w-full gap-3 p-4 text-left bg-n-solid-2 hover:bg-n-alpha-2"
              @click="toggleMonth(month.month)"
            >
              <span class="text-base font-semibold capitalize text-n-slate-12">
                {{ formatMonth(month.month) }}
              </span>
              <span
                class="px-2 py-1 text-xs rounded-full bg-n-teal-3 text-n-teal-11"
              >
                {{ month.sales_count }} ventas
              </span>
              <span
                class="px-2 py-1 text-xs rounded-full bg-n-amber-3 text-n-amber-11"
              >
                {{ month.no_sales_count }} no ventas
              </span>
              <span class="text-sm text-n-slate-11">
                {{ formatMoney(month.sales_amount) }}
              </span>
              <span
                class="px-2 py-1 text-xs rounded-full bg-n-blue-3 text-n-blue-11"
              >
                Comisión: {{ formatMoney(month.commission_amount) }}
              </span>
              <span
                v-if="month.pending_count"
                class="px-2 py-1 text-xs rounded-full bg-n-amber-3 text-n-amber-11"
              >
                {{ month.pending_count }} pendientes
              </span>
              <span
                v-if="month.invoiced_count"
                class="px-2 py-1 text-xs rounded-full bg-n-blue-3 text-n-blue-11"
              >
                {{ month.invoiced_count }} facturadas
              </span>
              <span
                v-if="month.paid_count"
                class="px-2 py-1 text-xs rounded-full bg-n-teal-3 text-n-teal-11"
              >
                {{ month.paid_count }} pagadas
              </span>
              <span class="ml-auto text-sm text-n-slate-11">
                {{ expandedMonths[month.month] ? 'Ocultar' : 'Ver detalle' }}
              </span>
            </button>

            <div v-if="expandedMonths[month.month]" class="overflow-x-auto">
              <div
                v-if="loadingMonths[month.month]"
                class="p-6 text-center text-n-slate-11"
              >
                Cargando operaciones...
              </div>

              <BaseTable
                v-else
                :headers="tableHeaders"
                :items="monthDetails[month.month] || []"
                no-data-message="No hay operaciones en este mes."
              >
                <template #row="{ items }">
                  <BaseTableRow
                    v-for="record in items"
                    :key="`month-${month.month}-${record.id}`"
                    :item="record"
                  >
                    <template #default>
                      <BaseTableCell>
                        <div class="font-medium text-n-slate-12">
                          #{{ record.id }}
                        </div>
                        <div class="text-xs text-n-slate-10">
                          {{ record.contact_name || 'Sin nombre' }}
                        </div>
                      </BaseTableCell>
                      <BaseTableCell>
                        {{ record.provider_name || '—' }}
                      </BaseTableCell>
                      <BaseTableCell>
                        {{ resultLabel(record.result) }}
                      </BaseTableCell>
                      <BaseTableCell>
                        {{
                          record.result === 'sale'
                            ? record.product_name
                            : noSaleReasonLabels[record.no_sale_reason] || '—'
                        }}
                      </BaseTableCell>
                      <BaseTableCell>
                        {{
                          record.result === 'sale'
                            ? formatMoney(record.product_amount)
                            : '—'
                        }}
                      </BaseTableCell>
                      <BaseTableCell>
                        {{
                          record.result === 'sale'
                            ? formatMoney(record.commission_amount)
                            : '—'
                        }}
                      </BaseTableCell>
                      <BaseTableCell>
                        {{ commissionStatusLabel(record.commission_status) }}
                      </BaseTableCell>
                      <BaseTableCell>
                        {{ formatDate(record.closed_at) }}
                      </BaseTableCell>
                      <BaseTableCell>
                        <div class="flex flex-wrap gap-1">
                          <Button
                            v-if="
                              record.result === 'sale' &&
                              record.commission_status === 'pending'
                            "
                            label="Facturar"
                            size="sm"
                            color="slate"
                            @click="markInvoiced([record.id])"
                          />
                          <Button
                            v-if="
                              record.result === 'sale' &&
                              record.commission_status === 'invoiced'
                            "
                            label="Marcar pagada"
                            size="sm"
                            color="slate"
                            @click="markPaid(record)"
                          />
                          <Button
                            label="Editar"
                            size="sm"
                            variant="ghost"
                            color="slate"
                            @click="openEditModal(record)"
                          />
                          <Button
                            label="Eliminar"
                            size="sm"
                            variant="ghost"
                            color="slate"
                            @click="deleteCommercialRecord(record)"
                          />
                        </div>
                      </BaseTableCell>
                    </template>
                  </BaseTableRow>
                </template>
              </BaseTable>
            </div>
          </div>
        </section>

        <section class="overflow-hidden border rounded-xl border-n-weak">
          <button
            type="button"
            class="flex items-center justify-between w-full gap-4 p-4 text-left bg-n-solid-2 hover:bg-n-alpha-2"
            @click="routingExpanded = !routingExpanded"
          >
            <div>
              <h3 class="text-lg font-semibold text-n-slate-12">
                Asignación de proveedores
              </h3>
              <p class="text-sm text-n-slate-11">
                Cambio inmediato, horarios y reasignación de pedidos.
              </p>
            </div>

            <span class="text-sm text-n-slate-11">
              {{ routingExpanded ? 'Ocultar' : 'Gestionar asignaciones' }}
            </span>
          </button>

          <div
            v-if="routingExpanded"
            class="flex flex-col gap-6 p-4 border-t border-n-weak"
          >
            <div
              v-for="route in providerRoutes"
              :key="route.city"
              class="p-4 border rounded-xl border-n-weak bg-n-solid-2"
            >
              <div v-if="routeDrafts[route.city]" class="flex flex-col gap-4">
                <div class="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div class="text-base font-semibold text-n-slate-12">
                      {{ route.city }} · {{ route.keyword }}
                    </div>
                    <div class="text-sm text-n-slate-11">
                      Proveedor activo:
                      <strong>
                        {{ route.active_provider_name || 'Sin proveedor' }}
                      </strong>
                    </div>
                  </div>

                  <span
                    class="px-2 py-1 text-xs rounded-full bg-n-teal-3 text-n-teal-11"
                  >
                    {{
                      routeDrafts[route.city].mode === 'manual'
                        ? 'Modo manual'
                        : 'Modo programado'
                    }}
                  </span>
                </div>

                <label class="flex flex-col gap-1 text-sm text-n-slate-11">
                  Modo de asignación
                  <select
                    v-model="routeDrafts[route.city].mode"
                    class="h-10 px-3 border rounded-lg border-n-weak bg-n-alpha-2 text-n-slate-12"
                  >
                    <option value="manual">Cambio manual inmediato</option>
                    <option value="scheduled">
                      Programado por días y horas
                    </option>
                  </select>
                </label>

                <label
                  v-if="routeDrafts[route.city].mode === 'manual'"
                  class="flex flex-col gap-1 text-sm text-n-slate-11"
                >
                  Proveedor activo
                  <select
                    v-model="routeDrafts[route.city].manual_provider_id"
                    class="h-10 px-3 border rounded-lg border-n-weak bg-n-alpha-2 text-n-slate-12"
                  >
                    <option value="" disabled>Selecciona un proveedor</option>
                    <option
                      v-for="provider in confirmedRoutingProviders"
                      :key="provider.id"
                      :value="provider.id"
                    >
                      {{ provider.name }}
                    </option>
                  </select>
                </label>

                <div v-else class="flex flex-col gap-4">
                  <label class="flex flex-col gap-1 text-sm text-n-slate-11">
                    Proveedor fuera de los horarios programados
                    <select
                      v-model="routeDrafts[route.city].fallback_provider_id"
                      class="h-10 px-3 border rounded-lg border-n-weak bg-n-alpha-2 text-n-slate-12"
                    >
                      <option value="" disabled>Selecciona un proveedor</option>
                      <option
                        v-for="provider in confirmedRoutingProviders"
                        :key="provider.id"
                        :value="provider.id"
                      >
                        {{ provider.name }}
                      </option>
                    </select>
                  </label>

                  <div
                    v-for="(schedule, scheduleIndex) in routeDrafts[route.city]
                      .schedules"
                    :key="`${route.city}-${scheduleIndex}`"
                    class="flex flex-col gap-3 p-3 border rounded-lg border-n-weak"
                  >
                    <div class="flex items-center justify-between gap-3">
                      <span class="text-sm font-medium text-n-slate-12">
                        Horario {{ scheduleIndex + 1 }}
                      </span>
                      <Button
                        label="Eliminar horario"
                        size="sm"
                        variant="ghost"
                        color="slate"
                        @click="removeSchedule(route.city, scheduleIndex)"
                      />
                    </div>

                    <label class="flex flex-col gap-1 text-sm text-n-slate-11">
                      Proveedor
                      <select
                        v-model="schedule.provider_id"
                        class="h-10 px-3 border rounded-lg border-n-weak bg-n-alpha-2 text-n-slate-12"
                      >
                        <option value="" disabled>
                          Selecciona un proveedor
                        </option>
                        <option
                          v-for="provider in confirmedRoutingProviders"
                          :key="provider.id"
                          :value="provider.id"
                        >
                          {{ provider.name }}
                        </option>
                      </select>
                    </label>

                    <div>
                      <div class="mb-2 text-sm text-n-slate-11">Días</div>
                      <div class="flex flex-wrap gap-2">
                        <button
                          v-for="day in dayOptions"
                          :key="day.value"
                          type="button"
                          class="w-9 h-9 text-sm border rounded-lg"
                          :class="
                            schedule.days.includes(day.value)
                              ? 'bg-n-blue-9 text-white border-n-blue-9'
                              : 'border-n-weak text-n-slate-11'
                          "
                          @click="toggleScheduleDay(schedule, day.value)"
                        >
                          {{ day.label }}
                        </button>
                      </div>
                    </div>

                    <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <label
                        class="flex flex-col gap-1 text-sm text-n-slate-11"
                      >
                        Desde
                        <input
                          v-model="schedule.start_time"
                          type="time"
                          class="h-10 px-3 border rounded-lg border-n-weak bg-n-alpha-2 text-n-slate-12"
                        />
                      </label>
                      <label
                        class="flex flex-col gap-1 text-sm text-n-slate-11"
                      >
                        Hasta
                        <input
                          v-model="schedule.end_time"
                          type="time"
                          class="h-10 px-3 border rounded-lg border-n-weak bg-n-alpha-2 text-n-slate-12"
                        />
                      </label>
                    </div>
                  </div>

                  <Button
                    label="Añadir horario"
                    size="sm"
                    variant="ghost"
                    color="slate"
                    @click="addSchedule(route.city)"
                  />
                </div>

                <div>
                  <Button
                    label="Guardar asignación"
                    size="sm"
                    :is-loading="savingRoutingCity === route.city"
                    @click="saveProviderRoute(route.city)"
                  />
                </div>
              </div>
            </div>

            <div class="p-4 border rounded-xl border-n-weak">
              <h4 class="text-base font-semibold text-n-slate-12">
                Reasignar un pedido existente
              </h4>
              <p class="mt-1 text-sm text-n-slate-11">
                El proveedor anterior dejará de verlo y el nuevo recibirá un
                aviso de asignación.
              </p>

              <div class="grid grid-cols-1 gap-4 mt-4 md:grid-cols-2">
                <label class="flex flex-col gap-1 text-sm text-n-slate-11">
                  Número de conversación
                  <input
                    v-model="reassignmentForm.conversation_id"
                    type="number"
                    min="1"
                    placeholder="Ejemplo: 125"
                    class="h-10 px-3 border rounded-lg border-n-weak bg-n-alpha-2 text-n-slate-12"
                  />
                </label>

                <label class="flex flex-col gap-1 text-sm text-n-slate-11">
                  Nuevo proveedor
                  <select
                    v-model="reassignmentForm.provider_id"
                    class="h-10 px-3 border rounded-lg border-n-weak bg-n-alpha-2 text-n-slate-12"
                  >
                    <option value="" disabled>Selecciona un proveedor</option>
                    <option
                      v-for="provider in confirmedRoutingProviders"
                      :key="provider.id"
                      :value="provider.id"
                    >
                      {{ provider.name }}
                    </option>
                  </select>
                </label>
              </div>

              <label
                class="flex items-center gap-2 mt-4 text-sm text-n-slate-11"
              >
                <input
                  v-model="reassignmentForm.resend_catalog"
                  type="checkbox"
                />
                Enviar al cliente el catálogo del nuevo proveedor
              </label>

              <div class="mt-4">
                <Button
                  label="Reasignar pedido"
                  size="sm"
                  color="slate"
                  :is-loading="isReassigningProvider"
                  @click="reassignProvider"
                />
              </div>
            </div>
          </div>
        </section>

        <section class="overflow-hidden border rounded-xl border-n-weak">
          <button
            type="button"
            class="flex items-center justify-between w-full gap-4 p-4 text-left bg-n-solid-2 hover:bg-n-alpha-2"
            @click="catalogsExpanded = !catalogsExpanded"
          >
            <div>
              <h3 class="text-lg font-semibold text-n-slate-12">
                Catálogos de proveedores
              </h3>
              <p class="text-sm text-n-slate-11">
                {{
                  providerCatalogs.filter(provider => provider.attached).length
                }}
                de {{ providerCatalogs.length }} proveedores con catálogo
              </p>
            </div>

            <span class="text-sm text-n-slate-11">
              {{ catalogsExpanded ? 'Ocultar' : 'Gestionar catálogos' }}
            </span>
          </button>

          <div v-if="catalogsExpanded" class="p-4 border-t border-n-weak">
            <p class="mb-4 text-sm text-n-slate-11">
              Sube, sustituye, descarga o elimina el catálogo PDF de cada
              proveedor.
            </p>

            <div
              v-if="!providerCatalogs.length"
              class="py-6 text-center text-n-slate-11"
            >
              No hay proveedores configurados.
            </div>

            <div v-else class="flex flex-col divide-y divide-n-weak">
              <div
                v-for="provider in providerCatalogs"
                :key="provider.provider_id"
                class="flex flex-wrap items-center justify-between gap-4 py-4"
              >
                <div class="min-w-0">
                  <div class="font-medium text-n-slate-12">
                    {{ provider.provider_name }}
                  </div>
                  <div v-if="provider.attached" class="text-sm text-n-slate-11">
                    {{ provider.filename }} ·
                    {{ formatFileSize(provider.byte_size) }}
                  </div>
                  <div v-else class="text-sm text-n-amber-11">
                    Sin catálogo asignado
                  </div>
                </div>

                <div class="flex flex-wrap items-center gap-2">
                  <label
                    class="inline-flex items-center h-8 px-3 text-sm font-medium border rounded-lg cursor-pointer border-n-weak text-n-slate-12 hover:bg-n-alpha-2"
                  >
                    {{
                      uploadingCatalogProviderId === provider.provider_id
                        ? 'Subiendo...'
                        : provider.attached
                          ? 'Sustituir PDF'
                          : 'Subir PDF'
                    }}
                    <input
                      type="file"
                      accept="application/pdf,.pdf"
                      class="hidden"
                      :disabled="
                        uploadingCatalogProviderId === provider.provider_id
                      "
                      @change="uploadProviderCatalog($event, provider)"
                    />
                  </label>

                  <Button
                    v-if="provider.attached"
                    label="Descargar"
                    size="sm"
                    variant="ghost"
                    color="slate"
                    :is-loading="
                      downloadingCatalogProviderId === provider.provider_id
                    "
                    @click="downloadProviderCatalog(provider)"
                  />

                  <Button
                    v-if="provider.attached"
                    label="Eliminar"
                    size="sm"
                    variant="ghost"
                    color="slate"
                    :is-loading="
                      deletingCatalogProviderId === provider.provider_id
                    "
                    @click="deleteProviderCatalog(provider)"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        <CommercialRecordEditModal
          ref="editModalRef"
          @submit="updateCommercialRecord"
        />
      </div>
    </template>
  </SettingsLayout>
</template>
